import * as React from 'react';
import { Box, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useTranslation } from 'react-i18next';
import { Logo } from '../common/Logo';
import { RailAccount } from './RailAccount';
import { RAIL } from './railTokens';

export const PHONE_HEADER_HEIGHT = 56;

/**
 * The phone header (handoff step 8, from Bennie-Holiday v2): back, the 28px mark, the app name and
 * the 36px account avatar.
 *
 * ⚠️ THE MARK SITS ON A BRAND TILE. `kx-mark.png` is a WHITE mark (plus a yellow dot) on transparent,
 * made for the rail's blue panel; drawn straight onto this white bar only the dot shows. The tile is
 * the rail's own surface (`RAIL.panelBackground`), so the one asset serves both and needs no
 * dark variant.
 *
 * Back is rendered only when the page has a parent step (`onBack`): a model goes to Models, a project
 * to its model (or Models when it has none). Dashboard and Models are top-level and show no back.
 * The rail keeps no back control (user ruling 2026-10-01); this header back is the phone's stand-in
 * for the trail, which is the desktop way up.
 */
export function PhoneHeader(props: {
  onBack?: () => void;
  onClearProjects?: () => void;
}) {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = React.useState(false);
  return (
    <Box
      component="header"
      data-testid="phone-header"
      sx={{
        flex: 'none',
        height: PHONE_HEADER_HEIGHT,
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        px: '12px',
        bgcolor: 'background.paper',
        borderBottom: (theme) => `1px solid ${theme.custom.colors.border.light}`,
      }}
    >
      {props.onBack && (
        <IconButton onClick={props.onBack} aria-label={t('common:actions.back')} data-testid="phone-back" size="small" sx={{ ml: '-4px' }}>
          <ArrowBackIcon />
        </IconButton>
      )}
      <Box
        data-testid="phone-header-logo"
        sx={{
          width: 36,
          height: 36,
          flex: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '10px',
          background: RAIL.panelBackground,
        }}
      >
        <Logo size={28} />
      </Box>
      <Box component="span" sx={{ flex: 1, minWidth: 0, fontSize: 18, fontWeight: 700, color: 'text.primary', whiteSpace: 'nowrap' }}>
        {t('nav:header.appName')}
      </Box>
      <RailAccount
        variant="phone"
        open={false}
        menuOpen={menuOpen}
        onMenuOpenChange={setMenuOpen}
        onClearProjects={props.onClearProjects}
      />
    </Box>
  );
}
